import { ArrowLeft, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { SECTIONS } from "./how-to-data";
import { SectionSearch } from "./section-search";

/** Smooth-scrolls the section with this id into view, if it exists on the page. */
function scrollToSection(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

interface HowToContentProps {
  /** Optional deep-link target from the `/how-to/:section` route param. */
  section?: string;
}

export function HowToContent({ section }: HowToContentProps) {
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeId, setActiveId] = useState<string>(SECTIONS[0]?.id ?? "");

  // Keyboard shortcut: Cmd+K to open search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Scroll-spy: track which section is currently in view and highlight its
  // TOC entry. The rootMargin shrinks the observed viewport band to a strip
  // near the top of the screen, so the section occupying that strip is the
  // one considered "active".
  useEffect(() => {
    const sectionEls = SECTIONS.map((s) => document.getElementById(s.id)).filter(
      (el): el is HTMLElement => el !== null,
    );

    const observer = new IntersectionObserver(
      (entries) => {
        const topmost = entries.find((entry) => entry.isIntersecting);
        if (topmost) {
          setActiveId(topmost.target.id);
        }
      },
      { rootMargin: "-96px 0px -60% 0px", threshold: 0 },
    );

    sectionEls.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Deep-link support: when landing on /how-to/:section (or the param
  // changes), smooth-scroll to that section's anchor.
  useEffect(() => {
    if (!section) {
      return;
    }
    scrollToSection(section);
  }, [section]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/home">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-1 size-4" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-primary text-3xl font-normal">How to Get Started</h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Everything you need to know about Scaffold UI
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setSearchOpen(true)}>
          <Search className="mr-2 size-4" />
          Search
          <kbd className="bg-primary/5 ml-2 rounded border px-1.5 py-0.5 text-[10px] font-medium">
            Cmd+K
          </kbd>
        </Button>
      </div>

      {/* Mobile tab strip — jumps to the section's anchor on the same page */}
      <div className="mb-6 flex gap-2 overflow-x-auto md:hidden">
        {SECTIONS.map((s) => (
          <Link
            key={s.id}
            to={`/how-to/${s.id}`}
            className={cn(
              "flex shrink-0 items-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium transition-colors",
              activeId === s.id
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-primary bg-transparent",
            )}
          >
            <s.icon className="size-3.5" />
            {s.title}
          </Link>
        ))}
      </div>

      {/* Desktop: sticky TOC + single scrolling page */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-[220px_1fr]">
        {/* Sticky table of contents — desktop only */}
        <nav className="hidden md:block">
          <div className="sticky top-6 flex max-h-[calc(100vh-3rem)] flex-col gap-1 overflow-y-auto">
            {SECTIONS.map((s) => (
              <Link
                key={s.id}
                to={`/how-to/${s.id}`}
                className={cn(
                  "flex items-center gap-2 border-l-2 px-3 py-2.5 text-left text-sm font-medium transition-colors",
                  activeId === s.id
                    ? "text-primary border-primary"
                    : "text-muted-foreground hover:text-primary border-transparent",
                )}
              >
                <s.icon className="size-4 shrink-0" />
                <span className="truncate">{s.title}</span>
              </Link>
            ))}
          </div>
        </nav>

        {/* Content — every section rendered top-to-bottom on a single page */}
        <div className="min-w-0 space-y-8">
          {SECTIONS.map((s) => (
            <section
              key={s.id}
              id={s.id}
              className="border-border bg-card scroll-mt-6 rounded-lg border p-6 shadow-sm sm:p-8"
            >
              <div className="mb-6 flex items-center gap-3">
                <div className="bg-accent/10 flex size-10 items-center justify-center rounded-lg">
                  <s.icon className="text-accent size-5" />
                </div>
                <div>
                  <h2 className="text-primary text-xl font-normal">{s.title}</h2>
                  <p className="text-muted-foreground text-sm">{s.description}</p>
                </div>
              </div>
              {s.content}
            </section>
          ))}
        </div>
      </div>

      {/* Search dialog */}
      <SectionSearch
        open={searchOpen}
        onOpenChange={setSearchOpen}
        onSelect={(sectionId) => {
          navigate(`/how-to/${sectionId}`);
          setSearchOpen(false);
        }}
      />
    </div>
  );
}
